import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { stringify } from 'qs';
import { ParameterService } from 'services/ParametersProviderApi/ParametersService';
import { AxiosWrapper } from 'services/AxiosWrapper';
import { injectJwtTokenIntoHeaders } from 'services/tokenLocalStorage';
import {
  IBookingsByDestinationRequest,
  ICrmMetricsRequest,
  ICrmMetricsResponse,
  IDestinationBreakdownResponse,
  ISalesMetricsFiltersRequest,
  ISalesMetricsFiltersResponse,
  ISalesMetricsRequest,
  ISalesMetricsResponse,
  ITopPerformersRequest,
  ITopPerformersResponse,
  ITaMetricsRequest,
  ITaMetricsResponse,
} from './types';

export enum DashboardEndpoints {
  SALES_METRICS = 'v1/sales-metrics',
  TOP_PERFORMERS = 'v1/top-performers',
  BOOKINGS_BY_DESTINATION = 'v1/bookings-by-destination',
  TA_METRICS = 'v1/ta-metrics',
  CRM_METRICS = 'v1/crm-metrics',
}

export class DashboardApiService<T extends AxiosWrapper<AxiosInstance>> {
  client: T;

  constructor(client: T) {
    this.client = client;
  }

  getSalesMetricsFilters = async (request: ISalesMetricsFiltersRequest): Promise<AxiosResponse<ISalesMetricsFiltersResponse>> => {
    const endpoint = `${DashboardEndpoints.SALES_METRICS}/filters`;
    const query = {
      ...request,
    };

    const response = await this.client.get(endpoint, { params: query });
    return response;
  };

  getSalesMetrics = async (request: ISalesMetricsRequest): Promise<AxiosResponse<ISalesMetricsResponse>> => {
    const endpoint = `${DashboardEndpoints.SALES_METRICS}`;
    const response = await this.client.post(endpoint, request);
    return response;
  };

  getTopPerformers = async (params: ITopPerformersRequest): Promise<AxiosResponse<ITopPerformersResponse>> => {
    const endpoint = `${DashboardEndpoints.TOP_PERFORMERS}`;
    const response = await this.client.post(endpoint, params);
    return response;
  };

  getBookingsByDestination = async (
    request: IBookingsByDestinationRequest,
  ): Promise<AxiosResponse<IDestinationBreakdownResponse>> => {
    const endpoint = `${DashboardEndpoints.BOOKINGS_BY_DESTINATION}`;
    const response = await this.client.post(endpoint, request);
    return response;
  };

  getTaMetrics = async (request: ITaMetricsRequest): Promise<AxiosResponse<ITaMetricsResponse>> => {
    const endpoint = `${DashboardEndpoints.TA_METRICS}`;
    const response = await this.client.get(endpoint, { params: request });
    return response;
  };

  getCrmMetrics = async (request: ICrmMetricsRequest): Promise<AxiosResponse<ICrmMetricsResponse>> => {
    const endpoint = DashboardEndpoints.CRM_METRICS;
    const response = await this.client.get(endpoint, { params: request });
    return response;
  };
}

export const makeDashboardApi = (): DashboardApiService<AxiosWrapper<AxiosInstance>> => {
  const dynamicParameters = ParameterService.getParameters();
  const headers: any = {};

  const client = axios.create({
    baseURL: dynamicParameters.REPORTING_DASHBOARD_BASE_URL,
    headers: injectJwtTokenIntoHeaders(headers),
    withCredentials: true,
    paramsSerializer: stringify,
  });

  const clientWrapper = new AxiosWrapper(client);

  return new DashboardApiService<AxiosWrapper<AxiosInstance>>(clientWrapper);
};
